import * as React from 'react';

import {ApiContextProps, withApi} from "../components/primitives/Context/ApiContext";

export const UserPreferencesContext = React.createContext<Partial<UserPreferencesContextProps>>({});

export interface PreferencesJSON {
  preferences?: { [index: string]: boolean | string };
  userConfigurations?: {
    configurations?: { [key: string]: Configurations };
    currentlySelected?: string;
  };
}

interface Configurations {
  configuration?: {
    engine?: {
      BBNSolver?: number;
      BDDConstructor?: number;
      OrderingRule?: number;
    };
    quantify?: {
      type?: string;
      targets?: string;
      mission_test_interval?: number;
      sampling?: {
        method?: string;
        number_of_samples?: number;
        confidence_level?: number;
      };
      importance?: {
        events?: string;
        measures?: string[];
      };
    };
  };
}

export enum PREFERENCES {
  THEME = 'theme',
  OUTLINE = 'outlineVisible',
  NODE_IDS_VISIBLE = 'nodeIdsVisible',
  NODE_VALUE_VISIBLE = 'node_value_visible',
  NODE_DESCRIPTION_ENABLED = 'nodeDescriptionEnabled'
}

export enum DEFAULTS {
  OUTLINE = 'true',
  THEME = 'Light',
  NODE_IDS_VISIBLE = 'false',
  NODE_VALUE_VISIBLE = 'false',
  NODE_DESCRIPTION_ENABLED = 'true'
}

export const defaultPreferences = {
  [PREFERENCES.THEME]: DEFAULTS.THEME,
  [PREFERENCES.OUTLINE]: DEFAULTS.OUTLINE,
  [PREFERENCES.NODE_IDS_VISIBLE]: DEFAULTS.NODE_IDS_VISIBLE,
  [PREFERENCES.NODE_DESCRIPTION_ENABLED]: DEFAULTS.NODE_DESCRIPTION_ENABLED,
};

export interface UserPreferencesContextProps {
  getPreference?: (name: string) => Partial<PreferencesJSON>;
  setPreference?: (key: string, value: any) => Promise<void>;
  provider?: PreferencesProvider;
}

interface PreferencesProviderProps extends ApiContextProps {}

interface State {
  userPreferences: PreferencesJSON;
  doesPreferenceExist: boolean;
}

export class PreferencesProvider extends React.Component<PreferencesProviderProps, State> {
  constructor(props: PreferencesProviderProps) {
    super(props);
    this.state = {
      userPreferences: {
        preferences: defaultPreferences,
      },
      doesPreferenceExist: false,
    };
  }

  componentDidMount(): void {
    this.getPreferences();
  }

  private getPreferences = () => {
    const { apiManager } = this.props;
    // @ts-ignore
    apiManager.getCurrentUserPreferences()
      .then((res: any) => res.ok ? res.json() : null)
      .then((data: PreferencesJSON) => data && this.setState({ userPreferences: data, doesPreferenceExist: true }));
  };

  private patchPreferences = (preferences: PreferencesJSON): Promise<PreferencesJSON> => {
    const { apiManager } = this.props;
    // @ts-ignore
    return apiManager.updateCurrentUserPreferences(JSON.stringify(preferences))
      .then((response: any) => response.json());
  };

  public getPreference = (key: string): any => {
    if(!key) {
      return null;
    }
    const { userPreferences: { preferences }} = this.state;
    const preference = preferences[key];
    // @ts-ignore
    return (preference === undefined || !preferences) ? defaultPreferences[key] : preference;
  };

  public setPreference = (key: string, value: any): Promise<void> => { // todo determine type
    const { userPreferences } = this.state;
    userPreferences.preferences[key] = value;
    return this.patchPreferences(userPreferences)
      .then((data: PreferencesJSON) => {
        this.setState(prevState => ({
          userPreferences: {
            ...prevState.userPreferences,
            ...data,
          }
        }))
      });
  };

  render() {
    const { children } = this.props;
    return (
      <UserPreferencesContext.Provider
        value={{
          getPreference: this.getPreference,
          setPreference: this.setPreference,
          provider: this,
        }}
      >
        {children}
      </UserPreferencesContext.Provider>
    );
  }
}

export const PreferencesConsumer = UserPreferencesContext.Consumer;

export const withPreferences = (WrappedComponent: any) => {
  return class extends React.Component<any, any> {
    render() {
      return (
        <PreferencesConsumer>
          {({ getPreference, setPreference, provider }) => (
            <WrappedComponent
              {...this.props}
              getPreference={getPreference}
              setPreference={setPreference}
              provider={provider}
            />
          )}
        </PreferencesConsumer>
      );
    }
  };
};

export default withApi(PreferencesProvider);
